/**
 * Phase B.5 — "Configure LLM keys" pre-flight gate.
 *
 * Wraps a chat input area. When the caller-org has zero LLM provider
 * keys configured (per `useOrgLLMKeysStatus`), the gate replaces
 * `children` with a card that explains the gap and deep-links to the
 * org-admin runtime-config page. While identity is still loading OR
 * the fetch fails (network / 5xx), the gate fails OPEN and renders
 * `children` unchanged — chat stays usable on transient failures.
 *
 * Mounted by:
 *   - `HomeLayout` — wraps the global-chat prompt-box block.
 *   - `CaseWorkspaceLayout` (`AIChatPane`) — wraps the per-case
 *     prompt-box (`AIEditorPromptBox` / `ChatInput`).
 *   - `ResearchChat` — wraps the per-topic research-mode `ChatInput`
 *     at `/research/:topicId` (Phase 6.4 surface).
 *
 * Other LLM-driven UI like `<ChatBriefingButton>` (one-shot briefing
 * trigger on client/matter detail screens) is intentionally NOT
 * wrapped — those are buttons, not chat surfaces. The plan §B.5
 * scope is "blocks chat", and the org-wide state is communicated
 * via the chat-screen gates above.
 *
 * The gate dismisses on the next 60s poll once an admin saves an LLM
 * key on `/org/config` — see `useOrgLLMKeysStatus.ts` for the cadence
 * + fail-open rationale.
 *
 * Closes the §1.5 production-mode UX gap. Production orgs don't get
 * the dev-mode env-var LLM-key fallback (Phase A.8 only seeds in
 * `NODE_ENV=development`), so a fresh-provisioned production org
 * has empty `runtimeConfig.llm.*` — without this gate, sending a
 * chat message would fail with a generic gateway error.
 */

import { KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import { useOrgLLMKeysStatus } from "@/hooks/useOrgLLMKeysStatus";

interface ChatLLMKeysGateProps {
  children: React.ReactNode;
}

export function ChatLLMKeysGate({ children }: ChatLLMKeysGateProps) {
  const { hasAnyKey } = useOrgLLMKeysStatus();

  // Fail-open posture: render children unless the answer is a
  // definitive `false` ("ready, and no keys"). Loading + error + the
  // pre-identity render all fall through to the chat input.
  if (hasAnyKey !== false) {
    return <>{children}</>;
  }

  return (
    <div
      data-testid="chat-llm-keys-gate"
      className="m-3 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900/50 dark:bg-amber-950/30"
    >
      <div className="flex items-start gap-3">
        <KeyRound
          size={18}
          className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold text-amber-900 dark:text-amber-200">
            Configure credenciais LLM para habilitar o chat
          </h3>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {/* Phase 5 Slice 5.5 (2026-05-20): widened to mention OAuth +
             *  OpenRouter alongside flat API keys. The deep-link target
             *  (`/org/config`) already exposes both subsections per
             *  Slice 5.2 (org-admin RuntimeConfigPage). */}
            Esta organização ainda não tem credenciais LLM configuradas.
            Adicione uma chave de API (Anthropic / OpenAI / Google / xAI),
            conecte uma assinatura OpenAI via OAuth (ChatGPT) ou configure
            um provedor OpenRouter para habilitar o assistente.
          </p>
        </div>
      </div>
      <div>
        <Link
          to="/org/config"
          data-testid="chat-llm-keys-gate-link"
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
        >
          Ir para Configurações
        </Link>
      </div>
    </div>
  );
}
